import { MAX_OUTPUT_SIDE, type BgRemovalConfig } from './config';
import { applyMask, fitWithin, parseHexColor, stretchMask } from './applyMask';
import type { WorkerRequest, WorkerResponse } from './protocol';

// ─────────────────────────────────────────────────────────────────
// Gränssnittet UI:t använder. Idag finns bara webbläsarvarianten; en
// framtida serverreserv (fas 2, se README) implementerar samma
// BackgroundRemover så att UI:t inte behöver ändras.
// ─────────────────────────────────────────────────────────────────

export type RemovalProgress = { phase: 'download'; percent: number } | { phase: 'inference' };

export interface BackgroundRemover {
  /** Returnerar den frilagda bilden (WebP, eller JPEG där WebP-export saknas). */
  removeBackground(source: Blob, onProgress?: (p: RemovalProgress) => void): Promise<Blob>;
}

export class BackgroundRemovalTimeout extends Error {
  constructor() { super('Bakgrundsborttagningen tog för lång tid'); }
}

let worker: Worker | null = null;
let nextId = 1;

function getWorker(): Worker {
  worker ??= new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
  return worker;
}

// En hängande eller kraschad worker kastas — nästa försök startar en ny
// (modellen ligger kvar i webbläsarens cache, så det går snabbt).
function resetWorker() {
  worker?.terminate();
  worker = null;
}

function canvasFor(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function readPixels(bitmap: ImageBitmap, width: number, height: number): ImageData {
  const ctx = canvasFor(width, height).getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas stöds inte');
  ctx.drawImage(bitmap, 0, 0, width, height);
  return ctx.getImageData(0, 0, width, height);
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise(resolve => canvas.toBlob(resolve, type, quality));
}

function runInWorker(config: BgRemovalConfig, image: ImageData, onProgress?: (p: RemovalProgress) => void) {
  const id = nextId++;
  const w = getWorker();
  return new Promise<{ data: Uint8Array; width: number; height: number }>((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = () => { clearTimeout(timer); w.removeEventListener('message', onMessage); w.removeEventListener('error', onError); };
    const onError = () => { finish(); resetWorker(); reject(new Error('Bildmodellen kunde inte startas')); };
    const onMessage = (event: MessageEvent<WorkerResponse>) => {
      const msg = event.data;
      if (msg.id !== id) return;
      if (msg.type === 'progress') {
        onProgress?.(msg.phase === 'download' ? { phase: 'download', percent: msg.percent } : { phase: 'inference' });
        // Tidsgränsen gäller själva körningen — första nedladdningen av
        // modellen kan ta längre tid på en långsam uppkoppling.
        if (msg.phase === 'inference' && timer === undefined) {
          timer = setTimeout(() => { finish(); resetWorker(); reject(new BackgroundRemovalTimeout()); }, config.timeoutMs);
        }
      } else if (msg.type === 'result') {
        finish();
        resolve(msg.mask);
      } else {
        finish();
        reject(new Error(msg.message));
      }
    };
    w.addEventListener('message', onMessage);
    w.addEventListener('error', onError);
    const request: WorkerRequest = { type: 'run', id, config, image: { data: image.data, width: image.width, height: image.height } };
    w.postMessage(request, [image.data.buffer]);
  });
}

export function createBrowserRemover(config: BgRemovalConfig): BackgroundRemover {
  return {
    async removeBackground(source, onProgress) {
      const bitmap = await createImageBitmap(source, { imageOrientation: 'from-image' });
      try {
        const full = fitWithin(bitmap.width, bitmap.height, MAX_OUTPUT_SIDE);
        const small = fitWithin(bitmap.width, bitmap.height, config.inputSize);
        const mask = await runInWorker(config, readPixels(bitmap, small.width, small.height), onProgress);

        const pixels = readPixels(bitmap, full.width, full.height);
        const out = applyMask(pixels, stretchMask(mask), parseHexColor(config.background));
        const canvas = canvasFor(full.width, full.height);
        canvas.getContext('2d')!.putImageData(new ImageData(out, full.width, full.height), 0, 0);

        // Safari kan inte exportera WebP och ger då PNG — välj JPEG istället
        // (bakgrunden är ogenomskinlig, så ingen transparens går förlorad).
        const webp = await toBlob(canvas, 'image/webp', 0.9);
        if (webp?.type === 'image/webp') return webp;
        const jpeg = await toBlob(canvas, 'image/jpeg', 0.92);
        if (!jpeg) throw new Error('Kunde inte spara den frilagda bilden');
        return jpeg;
      } finally {
        bitmap.close();
      }
    },
  };
}
